/**
 * PROMOCIONES (0.53.0, hub_031) — lo que la pantalla necesita decir igual que la app de pacientes (PROMOS-PRO §1.1, §2 y
 * §8): el beneficio en palabras, el precio con la promo, el estado de ahora (con fecha o sin fecha, con cupo o sin él, y
 * "hasta que pasen las dos") y cómo se lee la duración. La regla de verdad vive en el API (`api/src/lib/promos.ts`) y en
 * Loyaltyapp (`api/shared/promos.js`); esto es la MISMA cuenta para la vista previa en vivo, antes de guardar.
 */
export type Beneficio = 'descuento_pct' | 'descuento_monto' | 'precio' | 'puntos_extra' | 'cortesia';
export type Modo = 'compra' | 'cupon';
export type Termina = 'primero' | 'ambos';
export type TipoPieza = 'tratamiento' | 'paquete' | 'farmacia';
export type Escalon = { concepto: string; precio: number };
/** Una pieza del catálogo (de `promos/opciones`). */
export type PiezaCat = { tipo: TipoPieza; ref_id: string; nombre: string; escalones: Escalon[] };
/** Una pieza de la promo: una del catálogo (con su escalón o todos) o una categoría completa. */
export type PiezaPromo = { tipo: TipoPieza; ref_id: string; escalon: string | null } | { tipo: TipoPieza; todo: true };
export const esCategoria = (z: PiezaPromo): z is { tipo: TipoPieza; todo: true } => (z as any).todo === true;

export const TIPO_TXT: Record<TipoPieza, string> = { tratamiento: 'Tratamiento', paquete: 'Paquete', farmacia: 'Farmacia' };
export const CATEGORIA_TXT: Record<TipoPieza, string> = { tratamiento: 'Todos los tratamientos', paquete: 'Todos los paquetes', farmacia: 'Toda la farmacia' };
export const BENEFICIO_TXT: Record<Beneficio, string> = {
  descuento_pct: '% de descuento', descuento_monto: '$ de descuento', precio: 'Precio especial', puntos_extra: 'Puntos extra', cortesia: 'Sin costo (cortesía)',
};

const r2 = (n: number) => Math.round(n * 100) / 100;
/** MXN0: sin decimales si es entero ("$1,500"; "$1,500.50"). La misma de la app. */
export const pesos = (n: number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: Number(n) % 1 ? 2 : 0, maximumFractionDigits: Number(n) % 1 ? 2 : 0 }).format(Number(n) || 0);

/** "20 % de descuento" · "$300 de descuento" · "precio especial $1,500" · "+300 puntos" · "sin costo". */
export function textoBeneficio(beneficio: Beneficio, valor: number): string {
  if (beneficio === 'descuento_pct') return `${Number(valor)} % de descuento`;
  if (beneficio === 'descuento_monto') return `${pesos(Number(valor))} de descuento`;
  if (beneficio === 'precio') return `precio especial ${pesos(Number(valor))}`;
  if (beneficio === 'cortesia') return 'sin costo';
  return `+${Math.round(Number(valor))} puntos`;
}
export const mayus = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
export function precioPromo(beneficio: Beneficio, valor: number, lista: number): number {
  const l = Number(lista) || 0; const v = Number(valor);
  if (beneficio === 'descuento_pct') return r2(l * (1 - v / 100));
  if (beneficio === 'descuento_monto') return r2(Math.max(0, l - v));
  if (beneficio === 'precio') return r2(Math.min(l, v));
  if (beneficio === 'cortesia') return 0;
  return r2(l);
}
/** Un descuento en pesos que deja la pieza en 0 o menos no aplica a esa pieza. */
export const aplicaA = (beneficio: Beneficio, valor: number, lista: number) => beneficio !== 'descuento_monto' || Number(lista) > Number(valor);

export type EstadoPromo = 'programada' | 'activa' | 'agotada' | 'terminada' | 'cancelada';
type Tiempo = { estado?: string; inicio_at: string | Date; fin_at: string | Date | null; cupo: number | null; termina_cuando?: Termina | string | null };
/** El estado de ahora (§1.1): igual en el API y en la app. */
export function estadoPromo(p: Tiempo, usados = 0, ahora = new Date()): EstadoPromo {
  if (p.estado === 'cancelada') return 'cancelada';
  if (new Date(p.inicio_at) > ahora) return 'programada';
  const finPaso = !!p.fin_at && new Date(p.fin_at) <= ahora;
  const lleno = p.cupo !== null && p.cupo !== undefined && usados >= Number(p.cupo);
  if (p.termina_cuando === 'ambos') return finPaso && lleno ? 'terminada' : 'activa';
  if (finPaso) return 'terminada';
  if (lleno) return 'agotada';
  return 'activa';
}
/** Cuántos lugares quedan (null = sin tope: sin cupo, o "ambos" antes de la fecha). */
export function quedanDe(p: Tiempo, usados = 0, ahora = new Date()): number | null {
  if (p.cupo === null || p.cupo === undefined) return null;
  if (p.termina_cuando === 'ambos' && !(p.fin_at && new Date(p.fin_at) <= ahora)) return null;
  return Math.max(0, Number(p.cupo) - usados);
}

const TZ = 'America/Mexico_City';
/** "31 oct" (y la hora si no es fin de día): la fecha en el día de la clínica. */
export function fechaCorta(x: string | Date, conHora = false): string {
  const d = new Date(x);
  const dia = d.toLocaleDateString('es-MX', { timeZone: TZ, day: 'numeric', month: 'short' }).replace('.', '');
  if (!conHora) return dia;
  const h = d.toLocaleTimeString('es-MX', { timeZone: TZ, hour: 'numeric', minute: '2-digit' }).replace(/\s?a\.\s?m\./, ' a.m.').replace(/\s?p\.\s?m\./, ' p.m.');
  return `${dia}, ${h}`;
}
/** "2 h 15 min" · "45 min" · "3 días": cuánto falta. */
export function falta(x: string | Date, ahora = new Date()): string {
  const min = Math.max(0, Math.ceil((new Date(x).getTime() - ahora.getTime()) / 60000));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h${min % 60 ? ` ${min % 60} min` : ''}`;
  return `${Math.round(h / 24)} días`;
}
/** Lo de tiempo de la tarjeta (§8): "Termina en 3 h" (falta menos de un día) / "Hasta el 31 oct" / "Sin fecha de fin". */
export function tiempoCorto(p: Tiempo, usados = 0, ahora = new Date()): string {
  const est = estadoPromo(p, usados, ahora);
  if (est === 'programada') return `Empieza el ${fechaCorta(p.inicio_at, true)}`;
  if (est === 'terminada' || est === 'cancelada') return est === 'cancelada' ? 'Cancelada' : 'Ya terminó';
  if (p.termina_cuando === 'ambos' && p.fin_at && new Date(p.fin_at) <= ahora) return `Hasta vender ${p.cupo}`;
  if (!p.fin_at) return 'Sin fecha de fin';
  const ms = new Date(p.fin_at).getTime() - ahora.getTime();
  return ms < 86400000 ? `Termina en ${falta(p.fin_at, ahora)}` : `Hasta el ${fechaCorta(p.fin_at)}`;
}
/**
 * Cómo se dice la duración y la cantidad (§1.1, las mismas palabras que la app): "Sin fecha de fin" · "Termina el 31 oct
 * o al agotarse" · "Quedan 7 de 20" · "Hasta el 31 oct y hasta vender 20 · van 23" · "Termina al vender 20 · quedan 5".
 * `[tiempo, cantidad]`: cada parte puede ir sola (la tarjeta pone la cantidad en su pastilla de arriba).
 */
export function textoDuracion(p: Tiempo, usados = 0, ahora = new Date()): { tiempo: string; cantidad: string | null; junto: string } {
  const cupo = p.cupo === null || p.cupo === undefined ? null : Number(p.cupo);
  const finPaso = !!p.fin_at && new Date(p.fin_at) <= ahora;
  let tiempo: string; let cantidad: string | null = null;
  if (p.termina_cuando === 'ambos' && p.fin_at && cupo !== null) {
    if (!finPaso) { tiempo = `Hasta el ${fechaCorta(p.fin_at, true)} y hasta vender ${cupo}`; cantidad = `van ${usados}`; }
    else { tiempo = `Termina al vender ${cupo}`; cantidad = `quedan ${Math.max(0, cupo - usados)}`; }
  } else {
    tiempo = !p.fin_at ? 'Sin fecha de fin' : `Termina el ${fechaCorta(p.fin_at, true)}${cupo !== null ? ' o al agotarse' : ''}`;
    if (cupo !== null) cantidad = `Quedan ${Math.max(0, cupo - usados)} de ${cupo}`;
  }
  return { tiempo, cantidad, junto: cantidad ? `${tiempo} · ${cantidad}` : tiempo };
}

export const ESTADO_TXT: Record<EstadoPromo, [string, string, string]> = {
  activa: ['Activa', '#d1fae5', '#065f46'], programada: ['Programada', '#fef3c7', '#92400e'], agotada: ['Agotada', '#fce7f3', '#8E1A5F'],
  terminada: ['Terminada', '#f3f4f6', '#6b7280'], cancelada: ['Cancelada', '#f3f4f6', '#6b7280'],
};

/** Medidas de cada espacio (§3). */
export const ESPACIOS = {
  tarjeta: { nombre: 'Tarjeta', aspecto: 16 / 9, relacion: '16:9', ancho: 1280, alto: 720, maxKB: 400, zona: { desde: 0.6, texto: 'Aquí va el nombre de la promo' }, donde: 'En Recompensas, la lista de promociones' },
  detalle: { nombre: 'Al abrirla', aspecto: 4 / 5, relacion: '4:5', ancho: 1080, alto: 1350, maxKB: 500, zona: { desde: 2 / 3, texto: 'Aquí la tapa el contenido' }, donde: 'Arriba, al abrir la promo; se desvanece hacia abajo' },
  aviso: { nombre: 'Notificación', aspecto: 2, relacion: '2:1', ancho: 1440, alto: 720, maxKB: 350, zona: null as null | { desde: number; texto: string }, donde: 'Android (al expandir), Windows y la campana de la app' },
} as const;
export type Espacio = keyof typeof ESPACIOS;

/** El título y el mensaje del aviso al empezar, por default (los MISMOS que pone el API si se dejan vacíos). */
export function textosAviso(p: { nombre: string; beneficio: Beneficio; valor: number; cupo: number | null; modo: Modo; termina_cuando?: Termina | null }) {
  return {
    titulo: `Promoción: ${p.nombre.trim() || '…'}`.slice(0, 80),
    cuerpo: `${mayus(textoBeneficio(p.beneficio, p.valor))}${p.cupo && p.termina_cuando !== 'ambos' ? ` · sólo los primeros ${p.cupo}` : ''}. ${p.modo === 'cupon' ? 'Guarda tu cupón en Recompensas de tu app.' : 'Ábrela en Recompensas de tu app.'}`.slice(0, 300),
  };
}
/** Un id nuevo (las imágenes nacen con el id de la promo antes de guardarla). */
export function nuevoId(): string {
  try { if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID(); } catch { /* sin contexto seguro */ }
  const b = new Uint8Array(16); crypto.getRandomValues(b); b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
/** "2026-10-16T14:30" para un `<input type="datetime-local">` (hora del navegador). */
export const localIso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
